import {
  DocumentReaderReExtractState,
  DocumentReaderRunState,
  type IDocumentReaderRunView,
} from '@commenda/api/commenda-ai/document-reader/types';

import {
  DocumentReadingRunHelper,
  type DocumentReadingStepKind,
} from './DocumentReadingRunHelper';

/** The count the step running now moves through (files or pages; for understand and sort, a share of the
 * whole step out of SHARE_SCALE), for its ring, its "312 of 678" or "61%", and the time left. */
export interface IDocumentReadingStepMeasure {
  key: DocumentReadingStepKind | 'reRead';
  done: number;
  /** Null while the step's total is not known yet. */
  total: number | null;
}

/** When this page first saw the step running now and how far it had got, to time the rest from the rate seen since. */
export interface IDocumentReadingStepSample {
  key: IDocumentReadingStepMeasure['key'];
  firstSeenAt: number;
  firstDone: number;
}

/** The count of a reading run's current step and the time left on it, from the rate this page has seen
 * or the one the backend reports. */
export class DocumentReadingTimeLeftHelper {
  /** No time left is shown until the step has been watched this long and is this share done. */
  static readonly MIN_TIME_LEFT_WATCH_MS = 60_000;

  static readonly MIN_TIME_LEFT_SHARE = 0.05;

  /** Steps of several stages whose counters restart at each stage: they are measured as one share of
   * the whole step, out of SHARE_SCALE, and shown as a percent. */
  static readonly SHARE_STEP_KINDS: readonly IDocumentReadingStepMeasure['key'][] =
    ['understand', 'sort'];

  static readonly SHARE_SCALE = 1000;

  /** The count the step running now moves through, each step by its own counters: unzip files of the
   * archive's files, files checked of those unzipped, pages read, and for understand and sort their
   * share across their stages. Null for a step with no count yet. */
  static getStepMeasure(
    run: Readonly<IDocumentReaderRunView>
  ): IDocumentReadingStepMeasure | null {
    const { reExtract, counters } = run;
    if (reExtract?.state === DocumentReaderReExtractState.RUNNING)
      return DocumentReadingTimeLeftHelper.toMeasure(
        'reRead',
        reExtract.papersDone,
        reExtract.documentsToRedo
      );
    if (run.state !== DocumentReaderRunState.RUNNING) return null;
    const step = DocumentReadingRunHelper.getStep(run.stage);
    if (!step) return null;
    const counts: Readonly<
      Record<
        DocumentReadingStepKind,
        readonly [number | undefined, number | undefined]
      >
    > = {
      unzip: [counters.filesUnpacked, counters.filesExpected],
      checkFiles: [counters.filesChecked, counters.filesUnpacked],
      readPages: [
        counters.pagesRead,
        counters.pages > 0 ? counters.pages : run.estimate?.pagesToRead,
      ],
      understand: [
        counters.documentsUnderstood,
        counters.documentsToUnderstand,
      ],
      sort: [counters.companiesSorted, counters.companiesToSort],
    };
    const [done, total] = counts[step.kind];
    if (DocumentReadingTimeLeftHelper.isShareMeasure({ key: step.kind }))
      return DocumentReadingTimeLeftHelper.toShareMeasure(
        step.kind,
        step.stages.indexOf(run.stage),
        step.stages.length,
        done,
        total
      );
    return DocumentReadingTimeLeftHelper.toMeasure(step.kind, done, total);
  }

  /** True for a step measured as a share of its stages, which shows "61%" rather than "412 of 678". */
  static isShareMeasure(
    measure: Readonly<Pick<IDocumentReadingStepMeasure, 'key'>>
  ): boolean {
    return DocumentReadingTimeLeftHelper.SHARE_STEP_KINDS.includes(measure.key);
  }

  /** Keeps the first sight of a step until another step starts; null while no step has a count. */
  static nextStepSample(
    previous: Readonly<IDocumentReadingStepSample> | null,
    measure: Readonly<IDocumentReadingStepMeasure> | null,
    now: number
  ): IDocumentReadingStepSample | null {
    if (!measure) return null;
    if (previous?.key === measure.key) return previous;
    return { key: measure.key, firstSeenAt: now, firstDone: measure.done };
  }

  /** "About 20 min left" once the step has been watched a minute and is 5% done. The rate is the
   * backend's own reading speed when it sends one, otherwise the count's move since first seen. */
  static getTimeLeftText(
    measure: Readonly<IDocumentReadingStepMeasure> | null,
    sample: Readonly<IDocumentReadingStepSample> | null,
    now: number,
    reportedSecondsLeft: number | null
  ): string | null {
    if (
      !measure ||
      measure.total === null ||
      !sample ||
      sample.key !== measure.key
    )
      return null;
    const watchedMs = now - sample.firstSeenAt;
    if (
      watchedMs < DocumentReadingTimeLeftHelper.MIN_TIME_LEFT_WATCH_MS ||
      measure.done / measure.total <
        DocumentReadingTimeLeftHelper.MIN_TIME_LEFT_SHARE
    )
      return null;
    const doneSinceSeen = measure.done - sample.firstDone;
    const secondsLeft =
      reportedSecondsLeft ??
      (doneSinceSeen > 0
        ? ((measure.total - measure.done) / doneSinceSeen) * (watchedMs / 1000)
        : null);
    if (secondsLeft === null) return null;
    if (secondsLeft < 60) return 'Less than a minute left';
    return `About ${DocumentReadingTimeLeftHelper.formatMinutes(Math.round(secondsLeft / 60))} left`;
  }

  /** "48 min", "1 h 12 min", "2 h". */
  static formatMinutes(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
  }

  /** No done count is no count, so the ring spins until the backend sends one. A zero or missing total
   * is unknown, and then nothing done is no count either: the strip never says "0 of 0" or "0 files". */
  private static toMeasure(
    key: IDocumentReadingStepMeasure['key'],
    done: number | undefined,
    total: number | undefined
  ): IDocumentReadingStepMeasure | null {
    if (done === undefined) return null;
    const knownTotal = total !== undefined && total > 0 ? total : null;
    if (knownTotal !== null)
      return { key, done: Math.min(done, knownTotal), total: knownTotal };
    return done > 0 ? { key, done, total: null } : null;
  }

  /** (stage index × total + done) / (stages × total), out of SHARE_SCALE. A stage whose counters were
   * reset to 0 still counts the stages before it; the first stage with no total yet is no count. */
  private static toShareMeasure(
    key: IDocumentReadingStepMeasure['key'],
    stageIndex: number,
    stageCount: number,
    done: number | undefined,
    total: number | undefined
  ): IDocumentReadingStepMeasure | null {
    if (done === undefined || stageIndex < 0 || stageCount === 0) return null;
    const knownTotal = total !== undefined && total > 0 ? total : null;
    if (knownTotal === null && stageIndex === 0) return null;
    const stageShare =
      knownTotal === null ? 0 : Math.min(done, knownTotal) / knownTotal;
    return {
      key,
      done: Math.round(
        ((stageIndex + stageShare) / stageCount) *
          DocumentReadingTimeLeftHelper.SHARE_SCALE
      ),
      total: DocumentReadingTimeLeftHelper.SHARE_SCALE,
    };
  }
}
